import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ModernTableCard from '../components/ModernTableCard';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { 
    TrendingUp, DollarSign, Users, ChefHat, Clock,
    CreditCard, CheckCircle, ShoppingCart,
    Utensils, RefreshCcw, MapPin, CalendarCheck,
    AlertTriangle, Eye, Play, Timer, Zap, Bell,
    ArrowRight, UserCheck, Coffee, Star, X, Check,
    Pause, Radio, Plus, AlertCircle, Edit, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// ========================================
// UTILITY FUNCTIONS
// ========================================

const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return Math.round(num).toLocaleString('fr-FR').replace(/,/g, ' ');
};

const formatCurrency = (amount) => formatNumber(amount) + ' F';

const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('fr-FR', {
        timeZone: 'Africa/Libreville',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatDuration = (minutes) => {
    if (!minutes) return '0 min';
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h${m > 0 ? m : ''}`;
};

// ========================================
// STAT CARD - Indicateur compact
// ========================================

const StatCard = ({ title, value, subtitle, icon: Icon, status = 'default', onClick, pulse }) => {
    const colors = {
        green: 'bg-green-50 border-green-200 text-green-700',
        orange: 'bg-orange-50 border-orange-200 text-orange-700',
        red: 'bg-red-50 border-red-200 text-red-700',
        blue: 'bg-blue-50 border-blue-200 text-blue-700',
        purple: 'bg-purple-50 border-purple-200 text-purple-700',
        default: 'bg-white border-gray-200 text-gray-700'
    };
    
    const iconColors = {
        green: 'bg-green-100 text-green-600',
        orange: 'bg-orange-100 text-orange-600',
        red: 'bg-red-100 text-red-600',
        blue: 'bg-blue-100 text-blue-600',
        purple: 'bg-purple-100 text-purple-600',
        default: 'bg-gray-100 text-gray-600'
    };
    
    return (
        <div 
            className={cn(
                "p-3 rounded-xl border-2 transition-all",
                colors[status],
                onClick && "cursor-pointer hover:shadow-lg hover:scale-[1.02]",
                pulse && "animate-pulse"
            )}
            onClick={onClick}
        >
            <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-xs font-medium opacity-70 truncate">{title}</p>
                    <p className="text-2xl font-bold truncate">{value}</p>
                    {subtitle && <p className="text-xs opacity-60 truncate">{subtitle}</p>}
                </div>
                {Icon && (
                    <div className={cn("p-2 rounded-lg shrink-0", iconColors[status])}>
                        <Icon className="w-5 h-5" />
                    </div>
                )}
            </div>
        </div>
    );
};

// ========================================
// TABLE CARD - Vue table compacte
// ========================================

// ========================================
// NOTE: TableCard is now imported from ModernTableCard component
// The local TableCard has been replaced for consistent styling
// ========================================

// ========================================
// KITCHEN ORDER CARD - Commande cuisine
// ========================================

const KitchenOrderCard = ({ order, onMarkReady }) => {
    const waitTime = order.wait_time_minutes || order.wait_time || 0;
    const isUrgent = waitTime > 15;
    const isReady = order.kitchen_status === 'pret' || order.status === 'ready';
    const isProblem = order.kitchen_status === 'probleme';
    const isPreparing = order.kitchen_status === 'en_preparation';
    
    return (
        <div className={cn(
            "p-3 rounded-lg border-2 transition-all",
            isReady && "bg-green-50 border-green-300",
            isProblem && "bg-purple-50 border-purple-300",
            isUrgent && !isReady && !isProblem && "bg-red-50 border-red-300",
            isPreparing && !isUrgent && "bg-orange-50 border-orange-300",
            !isUrgent && !isReady && !isProblem && !isPreparing && "bg-blue-50 border-blue-200"
        )}>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <Badge variant={isUrgent ? "destructive" : "outline"} className="text-xs">
                        #{order.order_number || order.id?.slice(-4)}
                    </Badge>
                    <span className="text-sm font-medium">Table {order.table_number}</span>
                </div>
                <div className={cn(
                    "flex items-center gap-1 text-xs font-medium",
                    isUrgent ? "text-red-600" : "text-muted-foreground"
                )}>
                    <Timer className="w-3 h-3" />
                    {waitTime} min
                </div>
            </div>
            
            <div className="text-xs text-muted-foreground mb-2">
                {order.items?.slice(0, 3).map((item, i) => (
                    <span key={i}>
                        {i > 0 && ' • '}
                        {item.quantity}x {item.name}
                    </span>
                ))}
                {order.items?.length > 3 && <span> +{order.items.length - 3}</span>}
            </div>
            
            {!isReady && (
                <Button 
                    size="sm" 
                    className={cn(
                        "w-full h-8",
                        isUrgent ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"
                    )}
                    onClick={() => onMarkReady?.(order)}
                >
                    <Check className="w-3 h-3 mr-1" />
                    Marquer prêt
                </Button>
            )}
        </div>
    );
};

// ========================================
// REGISTER MINI CARD
// ========================================

const RegisterMiniCard = ({ register, onClick }) => {
    const isOpen = register.status === 'open';
    const isLocked = register.is_locked;
    
    return (
        <div 
            className={cn(
                "p-3 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md",
                isOpen && !isLocked && "bg-green-50 border-green-300",
                isOpen && isLocked && "bg-amber-50 border-amber-300",
                !isOpen && "bg-gray-50 border-gray-200"
            )}
            onClick={onClick}
        >
            <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm">{register.register_name}</span>
                {isLocked ? (
                    <Badge className="bg-amber-500 text-[10px]">Verrouillée</Badge>
                ) : isOpen ? (
                    <Badge className="bg-green-500 text-[10px]">Active</Badge>
                ) : (
                    <Badge variant="outline" className="text-[10px]">Fermée</Badge>
                )}
            </div>
            {isOpen && (
                <>
                    <p className="text-xs text-muted-foreground truncate">{register.assigned_user_name}</p>
                    <div className="flex items-center justify-between mt-1 text-xs">
                        <span className="text-muted-foreground">{register.orders_count || 0} cmd</span>
                        <span className="font-bold text-green-600">{formatCurrency(register.total_sales || 0)}</span>
                    </div>
                </>
            )}
        </div>
    );
};

// ========================================
// EMPLOYEE STATUS MINI
// ========================================

const EmployeeMini = ({ employee }) => {
    return (
        <div className={cn(
            "flex items-center gap-2 p-2 rounded-lg",
            employee.status === 'present' && "bg-green-50",
            employee.status === 'late' && "bg-amber-50",
            employee.status === 'absent' && "bg-red-50"
        )}>
            <div className={cn(
                "w-2 h-2 rounded-full",
                employee.status === 'present' && "bg-green-500",
                employee.status === 'late' && "bg-amber-500",
                employee.status === 'absent' && "bg-red-500"
            )} />
            <div className="min-w-0 flex-1">
                <p className="text-xs font-medium truncate">{employee.name}</p>
                <p className="text-[10px] text-muted-foreground truncate">{employee.role}</p>
            </div>
        </div>
    );
};

// ========================================
// ALERT ITEM
// ========================================

const AlertItem = ({ alert, onDismiss }) => {
    const typeConfig = {
        kitchen_delay: { icon: ChefHat, color: 'text-red-600 bg-red-50', label: 'Cuisine' },
        stock_low: { icon: AlertTriangle, color: 'text-orange-600 bg-orange-50', label: 'Stock' },
        register_issue: { icon: CreditCard, color: 'text-amber-600 bg-amber-50', label: 'Caisse' },
        service: { icon: Bell, color: 'text-blue-600 bg-blue-50', label: 'Service' }
    };
    
    const config = typeConfig[alert.type] || typeConfig.service;
    const Icon = config.icon;
    
    return (
        <div className={cn("p-2 rounded-lg flex items-start gap-2", config.color)}>
            <Icon className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
                <p className="text-xs font-medium">{alert.message}</p>
                <p className="text-[10px] opacity-70">{alert.time}</p>
            </div>
            {onDismiss && (
                <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0"
                    onClick={() => onDismiss(alert)}
                >
                    <X className="w-3 h-3" />
                </Button>
            )}
        </div>
    );
};

// ========================================
// MAIN DIRECTOR DASHBOARD
// ========================================

const DirectorDashboardPage = () => {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    
    // State
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [registers, setRegisters] = useState([]);
    const [tables, setTables] = useState([]);
    const [kitchenOrders, setKitchenOrders] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [servers, setServers] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [pendingClosuresCount, setPendingClosuresCount] = useState(0);
    
    // Dialogs
    const [assignServerDialog, setAssignServerDialog] = useState({ open: false, table: null });
    const [selectedServer, setSelectedServer] = useState('');
    const [selectedTable, setSelectedTable] = useState(null);
    const [orderViewOpen, setOrderViewOpen] = useState(false);
    const [orderDetails, setOrderDetails] = useState(null);
    const [loadingOrder, setLoadingOrder] = useState(false);
    
    const intervalRef = useRef(null);
    
    // ========================================
    // DATA FETCHING
    // ========================================
    
    const fetchAllData = useCallback(async (showToast = false) => {
        try {
            const [
                dashRes,
                registersRes,
                tablesRes,
                kitchenRes,
                employeesRes,
                staffRes,
                pendingClosuresRes
            ] = await Promise.all([
                api().get('/pos/admin/dashboard/v2?period=today').catch(() => ({ data: null })),
                api().get('/registers/dashboard').catch(() => ({ data: [] })),
                api().get('/tables').catch(() => ({ data: [] })),
                api().get('/kitchen/tickets').catch(() => ({ data: [] })),
                api().get('/attendance/today-all').catch(() => ({ data: [] })),
                api().get('/staff').catch(() => ({ data: [] })),
                // Utiliser le bon endpoint pour compter les clôtures en attente
                api().get('/finance/closures/pending-count').catch(() => ({ data: { pending_count: 0 } }))
            ]);
            
            setData(dashRes.data);
            setRegisters(registersRes.data || []);
            setTables(tablesRes.data || []);
            setKitchenOrders(kitchenRes.data || []);
            setEmployees(employeesRes.data || []);
            // Gérer les deux formats de réponse possibles
            const pendingData = pendingClosuresRes.data || pendingClosuresRes;
            setPendingClosuresCount(pendingData?.pending_count || 0);
            
            // Filter servers from staff
            const serverRoles = ['SERVEUR', 'EMPLOYE'];
            setServers((staffRes.data || []).filter(s => serverRoles.includes(s.role)));
            
            // Generate alerts
            const newAlerts = [];
            const urgentOrders = (kitchenRes.data || []).filter(o => o.wait_time > 15);
            if (urgentOrders.length > 0) {
                newAlerts.push({
                    id: 'kitchen_delay',
                    type: 'kitchen_delay',
                    message: `${urgentOrders.length} commande(s) en retard cuisine`,
                    time: 'Maintenant'
                });
            }
            setAlerts(newAlerts);
            
            setLastUpdate(new Date());
            
            if (showToast) {
                toast.success('Données actualisées');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            if (showToast) {
                toast.error('Erreur de chargement');
            }
        } finally {
            setLoading(false);
        }
    }, [api]);
    
    // Initial load
    useEffect(() => {
        fetchAllData();
    }, []);
    
    // Auto-refresh every 8 seconds
    useEffect(() => {
        if (autoRefresh) {
            intervalRef.current = setInterval(() => fetchAllData(false), 8000);
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [autoRefresh, fetchAllData]);
    
    // ========================================
    // COMPUTED VALUES
    // ========================================
    
    const financial = data?.financial || {};
    const operational = data?.operational || {};
    
    const openRegisters = registers.filter(r => r.status === 'open');
    const totalSales = openRegisters.reduce((sum, r) => sum + (r.total_sales || 0), 0);
    
    const tablesOccupied = tables.filter(t => (t.status === 'occupied' || t.status === 'occupee')).length;
    const tablesAvailable = tables.filter(t => t.status === 'available').length;
    const tablesReserved = tables.filter(t => t.status === 'reserved').length;
    const tablesWaiting = tables.filter(t => t.status === 'waiting_payment').length;
    
    const pendingOrders = kitchenOrders.filter(o => o.kitchen_status === 'nouveau' || o.status === 'pending');
    const preparingOrders = kitchenOrders.filter(o => o.kitchen_status === 'en_preparation' || o.status === 'preparing');
    const urgentOrders = kitchenOrders.filter(o => (o.wait_time_minutes || o.wait_time || 0) > 15);
    const readyOrders = kitchenOrders.filter(o => o.kitchen_status === 'pret' || o.status === 'ready');
    const problemOrders = kitchenOrders.filter(o => o.kitchen_status === 'probleme');
    
    const presentEmployees = employees.filter(e => e.status === 'present');
    const activeServers = employees.filter(e => 
        e.status === 'present' && ['SERVEUR', 'EMPLOYE'].includes(e.role)
    );
    const activeCooks = employees.filter(e => 
        e.status === 'present' && ['CUISINIER', 'CHEF_CUISINE'].includes(e.role)
    );
    
    // Chart data
    const chartData = data?.hourly_sales?.map(h => ({
        hour: `${h.hour}h`,
        ca: h.ca,
        commandes: h.commandes
    })) || [];
    
    // ========================================
    // HANDLERS
    // ========================================
    
    const handleTableClick = async (table) => {
        if ((table.status === 'occupied' || table.status === 'occupee') || table.status === 'occupee' || table.status === 'waiting_payment') {
            setSelectedTable(table);
            setLoadingOrder(true);
            setOrderViewOpen(true);
            try {
                const response = await api().get(`/pos/table/${table.number}/current-order`);
                setOrderDetails(response.data);
            } catch (error) {
                console.error('Erreur chargement commande:', error);
                toast.error('Impossible de charger la commande');
                setOrderDetails(null);
            } finally {
                setLoadingOrder(false);
            }
        } else {
            navigate(`/tables?selected=${table.id}`);
        }
    };

    // Handler for quick actions from ModernTableCard
    const handleQuickAction = async (action, table) => {
        switch (action) {
            case 'add':
                if (table.current_order_id) {
                    navigate(`/pos?table_id=${table.id}&order_id=${table.current_order_id}`);
                } else {
                    navigate(`/pos?table_id=${table.id}`);
                }
                break;
            case 'view':
                setSelectedTable(table);
                setLoadingOrder(true);
                setOrderViewOpen(true);
                try {
                    const response = await api().get(`/pos/table/${table.number}/current-order`);
                    setOrderDetails(response.data);
                } catch (error) {
                    toast.error('Impossible de charger la commande');
                    setOrderDetails(null);
                } finally {
                    setLoadingOrder(false);
                }
                break;
            case 'pay':
                goToPayment(table);
                break;
            case 'call':
                try {
                    await api().post('/service-notifications/call-manager', {
                        type: 'call_manager',
                        message: `Appel manager - Table ${table.number || table.name}`,
                        table_id: String(table.id),
                        table_number: String(table.number || table.name),
                        priority: 'high'
                    });
                    toast.success(`Manager appelé pour la table ${table.number || table.name}`);
                } catch (error) {
                    toast.error('Erreur lors de l\'appel du manager');
                }
                break;
            default:
                handleTableClick(table);
        }
    };

    const editOrder = (table) => {
        setOrderViewOpen(false);
        if (table?.current_order_id) {
            navigate(`/pos?table_id=${table.id}&order_id=${table.current_order_id}`);
        } else {
            navigate(`/pos?table=${table?.number || table?.id}`);
        }
    };

    const goToPayment = (table) => {
        setOrderViewOpen(false);
        navigate(`/pos?table=${table?.number || table?.id}&action=payment`);
    };
    
    const handleAssignServer = async () => {
        if (!selectedServer || !assignServerDialog.table) return;
        
        try {
            await api().put(`/tables/${assignServerDialog.table.id}/assign`, {
                server_id: selectedServer
            });
            toast.success('Serveur assigné');
            setAssignServerDialog({ open: false, table: null });
            setSelectedServer('');
            fetchAllData();
        } catch (error) {
            toast.error('Erreur lors de l\'assignation');
        }
    };
    
    const handleMarkOrderReady = async (order) => {
        try {
            const orderId = order.order_id || order.id;
            await api().put(`/kitchen/tickets/${orderId}/status`, { status: 'pret' });
            toast.success('Commande marquée prête');
            fetchAllData();
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    const handleRegisterClick = (register) => {
        navigate('/register-management');
    };
    
    // ========================================
    // RENDER
    // ========================================
    
    if (loading && !data) {
        return (
            <div className="flex items-center justify-center h-[60vh]">
                <RefreshCcw className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-3 text-muted-foreground">Chargement du tableau de bord...</span>
            </div>
        );
    }

    return (
        <div className="p-2 sm:p-4 lg:p-6 space-y-4 max-w-[1600px] mx-auto" data-testid="director-dashboard">
            {/* ========================================
                HEADER
            ======================================== */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                        <Zap className="w-6 h-6 text-blue-600" />
                        Pilotage du Service
                    </h1>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                        Vue opérationnelle en temps réel
                        {lastUpdate && (
                            <span className="ml-2 text-blue-600">
                                • {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        )}
                    </p>
                </div>
                
                <div className="flex items-center gap-2">
                    <Button 
                        variant={autoRefresh ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        className={autoRefresh ? "bg-blue-600" : ""}
                    >
                        {autoRefresh ? <Radio className="w-4 h-4 mr-1 animate-pulse" /> : <Pause className="w-4 h-4 mr-1" />}
                        {autoRefresh ? 'Live' : 'Pause'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => fetchAllData(true)}>
                        <RefreshCcw className={cn("w-4 h-4", loading && "animate-spin")} />
                    </Button>
                </div>
            </div>

            {/* ========================================
                ALERTS BAR
            ======================================== */}
            {alerts.length > 0 && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <span className="font-bold text-red-700">Alertes ({alerts.length})</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {alerts.map((alert, i) => (
                            <AlertItem key={alert.id || i} alert={alert} />
                        ))}
                    </div>
                </div>
            )}

            {/* ========================================
                ROW 1: KPIs PRINCIPAUX (8 cards)
            ======================================== */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                <StatCard
                    title="CA du jour"
                    value={financial.ca_formatted ? `${financial.ca_formatted}` : '0 F'}
                    icon={DollarSign}
                    status="green"
                    onClick={() => navigate('/daily-sales')}
                />
                <StatCard
                    title="Commandes"
                    value={operational.commandes_en_cours || 0}
                    subtitle="en cours"
                    icon={ShoppingCart}
                    status="blue"
                />
                <StatCard
                    title="Tables"
                    value={tablesOccupied}
                    subtitle={`${tablesAvailable} libres`}
                    icon={MapPin}
                    status={tablesOccupied > 0 ? "blue" : "default"}
                    onClick={() => navigate('/tables')}
                />
                <StatCard
                    title="Cuisine"
                    value={pendingOrders.length + preparingOrders.length}
                    subtitle="en prépa"
                    icon={ChefHat}
                    status={urgentOrders.length > 0 ? "red" : "orange"}
                    pulse={urgentOrders.length > 0}
                    onClick={() => navigate('/kitchen-dashboard')}
                />
                <StatCard
                    title="Prêtes"
                    value={operational.commandes_pretes || readyOrders.length}
                    subtitle="à servir"
                    icon={CheckCircle}
                    status="green"
                />
                <StatCard
                    title="Retard"
                    value={urgentOrders.length}
                    subtitle="> 15 min"
                    icon={Timer}
                    status={urgentOrders.length > 0 ? "red" : "green"}
                    pulse={urgentOrders.length > 0}
                />
                <StatCard
                    title="Caisses"
                    value={openRegisters.length}
                    subtitle="ouvertes"
                    icon={CreditCard}
                    status={openRegisters.length > 0 ? "green" : "default"}
                    onClick={() => navigate('/register-management')}
                />
                <StatCard
                    title="Équipe"
                    value={presentEmployees.length}
                    subtitle="présents"
                    icon={Users}
                    status="purple"
                    onClick={() => navigate('/today-attendance')}
                />
            </div>

            {/* ========================================
                ROW 2: MAIN PANELS
            ======================================== */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                
                {/* COLUMN 1: Salle (Tables) */}
                <Card className="lg:col-span-1">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-blue-600" />
                                Salle ({tablesOccupied}/{tables.length})
                            </CardTitle>
                            <Button size="sm" variant="ghost" onClick={() => navigate('/tables')}>
                                <Eye className="w-4 h-4" />
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                        {/* Stats rapides */}
                        <div className="grid grid-cols-4 gap-1 mb-3 text-center">
                            <div className="p-1.5 bg-green-50 rounded-lg">
                                <p className="text-lg font-bold text-green-600">{tablesAvailable}</p>
                                <p className="text-[9px] text-green-600">Libres</p>
                            </div>
                            <div className="p-1.5 bg-blue-50 rounded-lg">
                                <p className="text-lg font-bold text-blue-600">{tablesOccupied}</p>
                                <p className="text-[9px] text-blue-600">Occupées</p>
                            </div>
                            <div className="p-1.5 bg-purple-50 rounded-lg">
                                <p className="text-lg font-bold text-purple-600">{tablesReserved}</p>
                                <p className="text-[9px] text-purple-600">Réservées</p>
                            </div>
                            <div className="p-1.5 bg-amber-50 rounded-lg">
                                <p className="text-lg font-bold text-amber-600">{tablesWaiting}</p>
                                <p className="text-[9px] text-amber-600">Addition</p>
                            </div>
                        </div>
                        
                        {/* Grille des tables - Modern Cards */}
                        <ScrollArea className="h-[300px]">
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                {tables.map(table => (
                                    <ModernTableCard
                                        key={table.id}
                                        table={table}
                                        onClick={handleTableClick}
                                        onQuickAction={handleQuickAction}
                                        size="compact"
                                        currentUser={user}
                                    />
                                ))}
                            </div>
                        </ScrollArea>
                        
                        {/* Actions rapides */}
                        <div className="flex gap-2 mt-3">
                            <Button 
                                size="sm" 
                                variant="outline" 
                                className="flex-1 text-xs"
                                onClick={() => navigate('/reservations')}
                            >
                                <CalendarCheck className="w-3 h-3 mr-1" />
                                Réservations
                            </Button>
                            <Button 
                                size="sm" 
                                className="flex-1 text-xs bg-blue-600 hover:bg-blue-700"
                                onClick={() => navigate('/tables')}
                            >
                                <Plus className="w-3 h-3 mr-1" />
                                Ouvrir table
                            </Button>
                        </div>
                    </CardContent>
                </Card>
                
                {/* COLUMN 2: Cuisine */}
                <Card className="lg:col-span-1">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <ChefHat className="w-4 h-4 text-orange-600" />
                                Cuisine
                                {urgentOrders.length > 0 && (
                                    <Badge variant="destructive" className="text-[10px] animate-pulse">
                                        {urgentOrders.length} en retard!
                                    </Badge>
                                )}
                                {problemOrders.length > 0 && (
                                    <Badge className="bg-purple-500 text-[10px]">
                                        {problemOrders.length} problème(s)
                                    </Badge>
                                )}
                            </CardTitle>
                            <Button size="sm" variant="ghost" onClick={() => navigate('/kitchen-dashboard')}>
                                <Eye className="w-4 h-4" />
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                        {/* Stats cuisine */}
                        <div className="grid grid-cols-4 gap-2 mb-3 text-center">
                            <div className={cn(
                                "p-2 rounded-lg",
                                pendingOrders.length > 5 ? "bg-red-50" : "bg-blue-50"
                            )}>
                                <p className={cn(
                                    "text-xl font-bold",
                                    pendingOrders.length > 5 ? "text-red-600" : "text-blue-600"
                                )}>{pendingOrders.length}</p>
                                <p className="text-[10px] text-muted-foreground">En attente</p>
                            </div>
                            <div className="p-2 bg-orange-50 rounded-lg">
                                <p className="text-xl font-bold text-orange-600">{preparingOrders.length}</p>
                                <p className="text-[10px] text-muted-foreground">En prépa</p>
                            </div>
                            <div className="p-2 bg-green-50 rounded-lg">
                                <p className="text-xl font-bold text-green-600">{operational.commandes_pretes || readyOrders.length}</p>
                                <p className="text-[10px] text-muted-foreground">Prêtes</p>
                            </div>
                            <div className="p-2 bg-purple-50 rounded-lg">
                                <p className="text-xl font-bold text-purple-600">{problemOrders.length}</p>
                                <p className="text-[10px] text-muted-foreground">Problème</p>
                            </div>
                        </div>
                        
                        {/* Commandes */}
                        <ScrollArea className="h-[240px]">
                            <div className="space-y-2">
                                {kitchenOrders.length === 0 ? (
                                    <div className="text-center py-8">
                                        <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                                        <p className="text-sm text-green-600 font-medium">Cuisine à jour</p>
                                        <p className="text-xs text-muted-foreground">Aucune commande en attente</p>
                                    </div>
                                ) : (
                                    kitchenOrders.slice(0, 6).map(order => (
                                        <KitchenOrderCard
                                            key={order.id}
                                            order={order}
                                            onMarkReady={handleMarkOrderReady}
                                        />
                                    ))
                                )}
                            </div>
                        </ScrollArea>
                        
                        {/* Temps moyen */}
                        <div className="mt-3 p-2 bg-gray-50 rounded-lg flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Temps moyen préparation</span>
                            <span className={cn(
                                "text-sm font-bold",
                                (operational.delai_moyen || 0) > 15 ? "text-red-600" : "text-green-600"
                            )}>
                                {operational.delai_moyen || 0} min
                            </span>
                        </div>
                    </CardContent>
                </Card>
                
                {/* COLUMN 3: Caisses + Équipe */}
                <div className="space-y-4">
                    {/* Caisses */}
                    <Card>
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <CreditCard className="w-4 h-4 text-green-600" />
                                    Caisses ({openRegisters.length})
                                </CardTitle>
                                <div className="flex items-center gap-1">
                                    {pendingClosuresCount > 0 && (
                                        <Button 
                                            size="sm" 
                                            variant="ghost" 
                                            onClick={() => navigate('/pending-closures')}
                                            className="relative text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                            data-testid="pending-closures-btn"
                                        >
                                            <Bell className="w-4 h-4" />
                                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                                                {pendingClosuresCount}
                                            </span>
                                        </Button>
                                    )}
                                    <Button size="sm" variant="ghost" onClick={() => navigate('/register-management')}>
                                        <Eye className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                            <div className="grid grid-cols-2 gap-2 mb-3">
                                {registers.slice(0, 4).map(register => (
                                    <RegisterMiniCard
                                        key={register.register_id}
                                        register={register}
                                        onClick={() => handleRegisterClick(register)}
                                    />
                                ))}
                            </div>
                            <div className="p-2 bg-green-50 rounded-lg flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">Total ventes</span>
                                <span className="text-sm font-bold text-green-600">{formatCurrency(totalSales)}</span>
                            </div>
                        </CardContent>
                    </Card>
                    
                    {/* Équipe */}
                    <Card>
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Users className="w-4 h-4 text-purple-600" />
                                    Équipe en service
                                </CardTitle>
                                <Button size="sm" variant="ghost" onClick={() => navigate('/today-attendance')}>
                                    <Eye className="w-4 h-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                                <div className="p-2 bg-purple-50 rounded-lg">
                                    <p className="text-lg font-bold text-purple-600">{activeServers.length}</p>
                                    <p className="text-[10px] text-purple-600">Serveurs</p>
                                </div>
                                <div className="p-2 bg-orange-50 rounded-lg">
                                    <p className="text-lg font-bold text-orange-600">{activeCooks.length}</p>
                                    <p className="text-[10px] text-orange-600">Cuisiniers</p>
                                </div>
                                <div className="p-2 bg-green-50 rounded-lg">
                                    <p className="text-lg font-bold text-green-600">{openRegisters.length}</p>
                                    <p className="text-[10px] text-green-600">Caissiers</p>
                                </div>
                            </div>
                            <ScrollArea className="h-[120px]">
                                <div className="space-y-1">
                                    {presentEmployees.slice(0, 6).map(emp => (
                                        <EmployeeMini key={emp.staff_id || emp.id} employee={emp} />
                                    ))}
                                </div>
                            </ScrollArea>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* ========================================
                ROW 3: Ventes + Top Produits + Serveurs
            ======================================== */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Graphique ventes */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-green-600" />
                            Ventes du jour
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="h-[180px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} tickLine={false} interval={2} />
                                    <YAxis tick={{ fontSize: 10 }} tickLine={false} tickFormatter={v => v >= 1000 ? `${v/1000}k` : v} />
                                    <Tooltip formatter={(value) => [formatCurrency(value), 'CA']} />
                                    <Area type="monotone" dataKey="ca" stroke="#22c55e" fill="#22c55e" fillOpacity={0.3} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
                
                {/* Top produits */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Star className="w-4 h-4 text-amber-500" />
                            Top ventes
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="space-y-2">
                            {(data?.top_products || []).slice(0, 6).map((prod, i) => (
                                <div key={prod.id} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <span className={cn(
                                            "w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold",
                                            i === 0 && "bg-amber-100 text-amber-700",
                                            i === 1 && "bg-gray-100 text-gray-600",
                                            i === 2 && "bg-amber-50 text-amber-600",
                                            i > 2 && "bg-gray-50 text-gray-500"
                                        )}>
                                            {i + 1}
                                        </span>
                                        <span className="text-xs font-medium truncate max-w-[100px]">{prod.name}</span>
                                    </div>
                                    <Badge variant="outline" className="text-[10px]">{prod.quantity} vendus</Badge>
                                </div>
                            ))}
                            {(!data?.top_products || data.top_products.length === 0) && (
                                <p className="text-center text-sm text-muted-foreground py-4">
                                    Aucune vente aujourd'hui
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>
                
                {/* Performance serveurs */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <UserCheck className="w-4 h-4 text-blue-600" />
                            Performance serveurs
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="space-y-2">
                            {(data?.employees || []).slice(0, 6).map((emp, i) => (
                                <div key={emp.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <span className={cn(
                                            "w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold",
                                            i === 0 && "bg-green-100 text-green-700",
                                            i > 0 && "bg-gray-50 text-gray-500"
                                        )}>
                                            {i + 1}
                                        </span>
                                        <span className="text-xs font-medium truncate max-w-[100px]">{emp.name}</span>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs font-bold">{formatCurrency(emp.ca)}</p>
                                        <p className="text-[10px] text-muted-foreground">{emp.commandes} cmd</p>
                                    </div>
                                </div>
                            ))}
                            {(!data?.employees || data.employees.length === 0) && (
                                <p className="text-center text-sm text-muted-foreground py-4">
                                    Aucune donnée
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* ========================================
                QUICK ACTIONS BAR (Fixed)
            ======================================== */}
            <div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/95 backdrop-blur rounded-full px-4 py-2 shadow-xl border z-50">
                <Button 
                    size="sm" 
                    className="rounded-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => navigate('/pos')}
                >
                    <Plus className="w-4 h-4 mr-1" />
                    Nouvelle vente
                </Button>
                <Button 
                    size="sm" 
                    variant="outline"
                    className="rounded-full"
                    onClick={() => navigate('/tables')}
                >
                    <MapPin className="w-4 h-4 mr-1" />
                    Salle
                </Button>
                <Button 
                    size="sm" 
                    variant="outline"
                    className="rounded-full"
                    onClick={() => navigate('/kitchen-dashboard')}
                >
                    <ChefHat className="w-4 h-4 mr-1" />
                    Cuisine
                </Button>
                <Button 
                    size="sm" 
                    variant="outline"
                    className="rounded-full"
                    onClick={() => navigate('/register-management')}
                >
                    <CreditCard className="w-4 h-4 mr-1" />
                    Caisses
                </Button>
            </div>
            
            {/* Bottom padding */}
            <div className="h-16" />
            
            {/* ========================================
                DIALOG: Assign Server
            ======================================== */}
            <Dialog open={assignServerDialog.open} onOpenChange={(open) => !open && setAssignServerDialog({ open: false, table: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Assigner un serveur à la table {assignServerDialog.table?.number || assignServerDialog.table?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <Select value={selectedServer} onValueChange={setSelectedServer}>
                            <SelectTrigger>
                                <SelectValue placeholder="Sélectionner un serveur" />
                            </SelectTrigger>
                            <SelectContent>
                                {activeServers.map(server => (
                                    <SelectItem key={server.staff_id || server.id} value={server.staff_id || server.id}>
                                        {server.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAssignServerDialog({ open: false, table: null })}>
                            Annuler
                        </Button>
                        <Button onClick={handleAssignServer} disabled={!selectedServer}>
                            Assigner
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ========================================
                DIALOG: Order View (Consultation)
            ======================================== */}
            <Dialog open={orderViewOpen} onOpenChange={setOrderViewOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader className="pb-2">
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-primary" />
                            Commande - Table {selectedTable?.name || selectedTable?.number}
                        </DialogTitle>
                        <DialogDescription>
                            {selectedTable?.status === 'a_debarrasser' ? 'Commande payée' : 'Consultation de la commande'}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <ScrollArea className="flex-1 pr-4">
                        {loadingOrder ? (
                            <div className="flex items-center justify-center py-12">
                                <RefreshCcw className="w-8 h-8 animate-spin text-muted-foreground" />
                            </div>
                        ) : orderDetails?.has_order ? (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg">
                                    <div>
                                        <p className="text-xs text-muted-foreground">N° Commande</p>
                                        <p className="font-bold text-lg">#{orderDetails.order?.order_number || orderDetails.order?.id?.slice(-6)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Statut</p>
                                        <Badge variant={orderDetails.order?.status === 'paid' ? 'success' : 'default'}>
                                            {orderDetails.order?.status === 'paid' ? 'Payée' : 'En cours'}
                                        </Badge>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Statut Cuisine</p>
                                        <Badge variant="outline" className={
                                            orderDetails.order?.kitchen_status === 'pret' ? 'bg-green-50 text-green-700' :
                                            orderDetails.order?.kitchen_status === 'en_preparation' ? 'bg-amber-50 text-amber-700' :
                                            'bg-blue-50 text-blue-700'
                                        }>
                                            <ChefHat className="w-3 h-3 mr-1" />
                                            {orderDetails.order?.kitchen_status === 'pret' ? 'Prêt' :
                                             orderDetails.order?.kitchen_status === 'en_preparation' ? 'En préparation' :
                                             orderDetails.order?.kitchen_status || 'En attente'}
                                        </Badge>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Couverts</p>
                                        <p className="font-medium">{orderDetails.order?.covers || 1} personne(s)</p>
                                    </div>
                                    {orderDetails.server?.name && (
                                        <div className="col-span-2">
                                            <p className="text-xs text-muted-foreground">Serveur</p>
                                            <p className="font-medium">{orderDetails.server.name}</p>
                                        </div>
                                    )}
                                </div>
                                
                                <div>
                                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                                        <ShoppingCart className="w-4 h-4" />
                                        Articles ({orderDetails.items?.length || 0})
                                    </h4>
                                    <div className="space-y-2">
                                        {orderDetails.items?.map((item, idx) => (
                                            <div key={idx} className="flex items-start justify-between p-3 bg-muted/30 rounded-lg border">
                                                <div className="flex items-start gap-3">
                                                    <Badge variant="outline" className="w-8 h-8 flex items-center justify-center font-bold">
                                                        {item.quantity || 1}
                                                    </Badge>
                                                    <div>
                                                        <p className="font-medium">{item.name || item.product_name}</p>
                                                        {item.notes && <p className="text-xs text-amber-600 italic">Note: {item.notes}</p>}
                                                    </div>
                                                </div>
                                                <p className="font-semibold">
                                                    {(item.subtotal || item.total || ((item.unit_price || item.price || 0) * (item.quantity || 1))).toLocaleString()} F
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                <div className="border-t pt-4">
                                    <div className="flex items-center justify-between text-xl font-bold">
                                        <span>Total</span>
                                        <span className="text-primary">{(orderDetails.total || 0).toLocaleString()} F</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12 text-muted-foreground">
                                <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p>Aucune commande sur cette table</p>
                            </div>
                        )}
                    </ScrollArea>
                    
                    <DialogFooter className="border-t pt-4 gap-2 flex-col sm:flex-row">
                        <Button variant="outline" onClick={() => setOrderViewOpen(false)} className="w-full sm:w-auto">
                            Fermer
                        </Button>
                        {orderDetails?.has_order && (
                            <>
                                <Button variant="secondary" onClick={() => editOrder(selectedTable)} className="w-full sm:flex-1">
                                    <Edit className="w-4 h-4 mr-2" />
                                    Modifier
                                </Button>
                                <Button onClick={() => goToPayment(selectedTable)} className="w-full sm:flex-1">
                                    <CreditCard className="w-4 h-4 mr-2" />
                                    Encaisser
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DirectorDashboardPage;
